import { useState, useEffect } from "react";
import { Music, Play, Trophy, Star, Users, Clock, Wine } from "lucide-react";
import { IMAGES } from "@/lib/constants";
import { Reveal, Badge, GlitchDivider } from "@/components/site/primitives";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

const SCHEDULE = [
  { time: "03:00 PM", title: "VIP Early Entry & Comic Alley Preview", note: "Spider-Society doors open early." },
  { time: "04:00 PM", title: "Opening DJ Set — Afrobeat & Hip-Hop", note: "Live graffiti murals begin." },
  { time: "05:30 PM", title: "Acrobatic Stunt & Breakdance Spider-Cipher", note: "Showcase battle on the main stage." },
  { time: "06:15 PM", title: "'Suit Up Your Way' Cosplay Runway & Contest", note: "Five costume categories judged live." },
  { time: "07:15 PM", title: "Live Muralist Showcase & Art Auction Finale", note: "Bid on original multiverse art." },
  { time: "08:00 PM", title: "Jole Live Spider-Punk Set & Glitch Dance Party", note: "Headline set closes the night." },
];

const CATEGORIES = [
  { icon: Star, name: "Best Streetwear Miles" },
  { icon: Music, name: "Best Spider-Punk" },
  { icon: Trophy, name: "Best Original Spider-Sona" },
  { icon: Star, name: "Best Spider-Woman / Gwen" },
  { icon: Users, name: "Best Family / Duo Squad" },
];

const MOCKTAILS = [
  { name: "The Leap of Faith", desc: "Blue raspberry & spun cotton candy", color: "#00f0ff" },
  { name: "Sunflower Boba", desc: "Golden citrus, honey & brown-sugar pearls", color: "#ffe600" },
  { name: "Spider-Punk Punch", desc: "Electric berry, hibiscus & lime fizz", color: "#ff0055" },
  { name: "Peter B.'s Cold Brew", desc: "Vanilla oat cold brew, lightly sweet", color: "#a0a0b0" },
];

function RunwayModal({ open, onOpenChange }) {
  const [form, setForm] = useState({ name: "", email: "", category: "", characterConcept: "" });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target?.value ?? e }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.category) return toast.error("Pick a category");
    setLoading(true);
    try {
      await api.post("/runway", form);
      toast.success("You're on the runway list! Check your email for details.");
      onOpenChange(false);
      setForm({ name: "", email: "", category: "", characterConcept: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Try again");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="runway-modal" className="bg-[#101018] border-white/10 text-white">
        <DialogHeader>
          <DialogTitle className="font-display text-3xl uppercase chromatic">Register For The Runway</DialogTitle>
          <DialogDescription className="text-white/50 font-head">Claim your spot on the multiverse catwalk. We'll email you the check-in details.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4 mt-2">
          <input data-testid="runway-name" required placeholder="Your name" value={form.name} onChange={set("name")} className="w-full bg-[#08080c] border border-white/15 rounded-lg px-4 py-3 outline-none focus:border-[#00f0ff]" />
          <input data-testid="runway-email" required type="email" placeholder="Email" value={form.email} onChange={set("email")} className="w-full bg-[#08080c] border border-white/15 rounded-lg px-4 py-3 outline-none focus:border-[#00f0ff]" />
          <Select value={form.category} onValueChange={(v) => setForm((f) => ({ ...f, category: v }))}>
            <SelectTrigger data-testid="runway-category" className="bg-[#08080c] border-white/15 h-12"><SelectValue placeholder="Choose category" /></SelectTrigger>
            <SelectContent className="bg-[#101018] border-white/10 text-white">
              {CATEGORIES.map((c) => <SelectItem key={c.name} value={c.name}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <textarea data-testid="runway-concept" placeholder="Describe your costume / Spider-Sona concept" value={form.characterConcept} onChange={set("characterConcept")} rows={3} className="w-full bg-[#08080c] border border-white/15 rounded-lg px-4 py-3 outline-none focus:border-[#00f0ff]" />
          <button data-testid="runway-submit" disabled={loading} className="w-full bg-[#ff0055] text-white font-head font-bold uppercase py-3 rounded-full glow-magenta hover:scale-[1.02] transition-transform disabled:opacity-60">
            {loading ? "Sending…" : "Suit Up →"}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function Entertainment() {
  const [modal, setModal] = useState(false);

  useEffect(() => {
    if (window.location.hash === "#runway") {
      setTimeout(() => document.getElementById("runway")?.scrollIntoView({ behavior: "smooth" }), 400);
    }
  }, []);

  return (
    <div className="pt-16 md:pt-20">
      {/* HERO */}
      <section className="relative min-h-[60vh] flex items-end overflow-hidden">
        <img src={IMAGES.spiderPunk} alt="Spider-Punk" className="absolute inset-0 w-full h-full object-cover opacity-50" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#08080c] via-[#08080c]/60 to-transparent" />
        <div className="absolute inset-0 halftone-magenta opacity-25" />
        <div className="relative max-w-7xl mx-auto w-full px-5 md:px-8 pb-16">
          <Badge color="magenta">The Lineup</Badge>
          <h1 className="font-display text-6xl md:text-8xl uppercase mt-4">Performance<br /><span className="text-[#00f0ff]">& Chaos</span></h1>
        </div>
      </section>

      {/* HEADLINER */}
      <section className="max-w-7xl mx-auto px-5 md:px-8 py-20 md:py-28">
        <div className="grid md:grid-cols-2 gap-10 items-center">
          <Reveal>
            <div className="relative">
              <img src={IMAGES.concert} alt="Jole live" className="rounded-2xl w-full h-[420px] object-cover glass-chroma" />
              <div className="absolute -bottom-5 -right-4 rotate-3"><Badge color="yellow">Headliner</Badge></div>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="font-mono2 text-xs tracking-[0.3em] text-[#ff0055] uppercase mb-3">Featured Spotlight</div>
            <h2 className="font-display text-5xl md:text-7xl uppercase">Jole</h2>
            <p className="font-head text-lg text-[#00f0ff] mt-1">Live Spider-Punk (Hobie Brown) Electric Energy Set</p>
            <p className="text-white/70 font-head mt-5 leading-relaxed">
              Denver's own genre-bending force channels the raw, anti-establishment energy of the Spider-Verse's
              most rebellious hero. Expect a collision of punk, afrobeat, hip-hop and glitchy multiverse
              soundscapes that turns the Truss House into a dimension-hopping dance floor.
            </p>
            <button data-testid="headliner-preview" className="mt-6 inline-flex items-center gap-3 glass-chroma rounded-full pl-2 pr-6 py-2 hover:scale-105 transition-transform">
              <span className="w-11 h-11 rounded-full bg-[#ff0055] grid place-items-center"><Play size={18} fill="white" className="text-white ml-0.5" /></span>
              <span className="font-head font-semibold">Preview the set</span>
            </button>
          </Reveal>
        </div>
      </section>

      <GlitchDivider />

      {/* TIMELINE */}
      <section className="max-w-5xl mx-auto px-5 md:px-8 py-20 md:py-28">
        <Reveal>
          <div className="font-mono2 text-xs tracking-[0.3em] text-[#ffe600] uppercase mb-3">Run of Show</div>
          <h2 className="font-display text-5xl md:text-7xl uppercase mb-14">The Timeline</h2>
        </Reveal>
        <div className="relative pl-8 md:pl-12">
          <div className="absolute left-2 md:left-3 top-2 bottom-2 w-[2px] bg-gradient-to-b from-[#00f0ff] via-[#ff0055] to-[#ffe600]" />
          {SCHEDULE.map((s, i) => (
            <Reveal key={s.time} delay={i * 0.05}>
              <div className="relative mb-10 group" data-testid={`timeline-item-${i}`}>
                <span className="absolute -left-[26px] md:-left-[38px] top-1.5 w-4 h-4 rounded-full bg-[#08080c] border-2 border-[#00f0ff] group-hover:bg-[#ff0055] group-hover:border-[#ff0055] transition-colors glow-cyan" />
                <div className="flex items-center gap-3 mb-1">
                  <Clock size={14} className="text-[#ff0055]" />
                  <span className="font-mono2 text-sm text-[#00f0ff] tracking-widest">{s.time}</span>
                </div>
                <h3 className="font-head font-bold text-xl md:text-2xl">{s.title}</h3>
                <p className="text-white/60 font-head text-sm mt-1">{s.note}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* RUNWAY */}
      <section id="runway" className="max-w-7xl mx-auto px-5 md:px-8 py-20 md:py-28 scroll-mt-24">
        <div className="glass-chroma rounded-3xl p-8 md:p-14 relative overflow-hidden">
          <div className="absolute inset-0 halftone opacity-20" />
          <div className="relative">
            <Reveal>
              <Badge color="cyan">Suit Up Your Way</Badge>
              <h2 className="font-display text-5xl md:text-7xl uppercase mt-4 mb-2">Cosplay Runway</h2>
              <p className="text-white/70 font-head max-w-2xl">
                Strut the multiverse catwalk in front of a live crowd &amp; judges. Foam &amp; plastic props only —
                all peace-bonded at check-in. Five categories, one champion per dimension.
              </p>
            </Reveal>
            <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4 mt-10">
              {CATEGORIES.map((c, i) => (
                <Reveal key={c.name} delay={i * 0.06}>
                  <div className="glass rounded-xl p-5 h-full hover:border-[#ff0055]/50 transition-colors">
                    <c.icon className="text-[#ffe600] mb-3" />
                    <div className="font-head font-semibold text-sm">{c.name}</div>
                  </div>
                </Reveal>
              ))}
            </div>
            <button data-testid="open-runway-modal" onClick={() => setModal(true)} className="mt-10 inline-flex items-center gap-2 bg-[#ff0055] text-white font-head font-bold uppercase px-8 py-4 rounded-full glow-magenta hover:scale-105 active:scale-95 transition-transform">
              Register For Runway
            </button>
          </div>
        </div>
      </section>

      {/* MOCKTAILS */}
      <section className="max-w-7xl mx-auto px-5 md:px-8 pb-24">
        <Reveal>
          <div className="flex items-center gap-3 mb-3">
            <Wine className="text-[#00f0ff]" />
            <div className="font-mono2 text-xs tracking-[0.3em] text-[#00f0ff] uppercase">100% Non-Alcoholic</div>
          </div>
          <h2 className="font-display text-5xl md:text-7xl uppercase mb-4">Craft Mocktail Bar</h2>
        </Reveal>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5 mt-8">
          {MOCKTAILS.map((m, i) => (
            <Reveal key={m.name} delay={i * 0.07}>
              <div className="glass-chroma rounded-2xl overflow-hidden group h-full" data-testid={`mocktail-${i}`}>
                <div className="h-44 overflow-hidden">
                  <img src={i % 2 ? IMAGES.mocktail2 : IMAGES.mocktail} alt={m.name} className="w-full h-full object-cover opacity-70 group-hover:scale-110 group-hover:opacity-90 transition-all duration-500" />
                </div>
                <div className="p-5">
                  <h3 className="font-display text-2xl uppercase" style={{ color: m.color }}>{m.name}</h3>
                  <p className="text-white/60 font-head text-sm mt-1">{m.desc}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <RunwayModal open={modal} onOpenChange={setModal} />
    </div>
  );
}
